/**
 * InstallCTASection — iter125c.
 *
 * Prominent Android + iPhone install CTA shown on the public LandingPage.
 * Detects the platform and renders the appropriate flow:
 *   • Android Chrome (or any browser firing `beforeinstallprompt`)
 *     → big primary button that calls the native install prompt
 *   • iPhone Safari (no install API)
 *     → expand-on-tap card with the Add-to-Home-Screen steps
 *   • Already installed (display-mode: standalone)
 *     → small "You're using the app — great!" badge instead
 *   • Other desktop / fallback
 *     → "Open this page on your phone" QR-style hint
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Share2, PlusSquare, Apple, Smartphone, CheckCircle2, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true
  );
}

export default function InstallCTASection() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [iosExpanded, setIosExpanded] = useState(false);
  const [androidExpanded, setAndroidExpanded] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const installedHandler = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleAndroidInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setInstalled(true);
      setDeferredPrompt(null);
    } else {
      // No prompt yet — open the inline how-to.
      setAndroidExpanded(true);
    }
  };

  if (installed) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="my-8 text-center"
        data-testid="install-cta-already-installed"
      >
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs font-semibold">
          <CheckCircle2 className="w-3.5 h-3.5" /> You're already using the app — nice.
        </span>
      </motion.div>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.4 }}
      className="my-8"
      data-testid="install-cta-section"
    >
      <div className="text-center mb-4">
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-primary font-bold">
          <Sparkles className="w-3 h-3" /> Install in 10 seconds
        </span>
        <h2 className="font-display text-xl sm:text-2xl font-bold mt-1">
          Use SplitEasy like a native app
        </h2>
        <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
          No App Store, no Play Store. Add to your home screen — works offline, fits in your dock.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
        {/* Android card */}
        <Card
          className={`p-4 rounded-2xl border-2 ${!ios ? 'border-primary/40 bg-primary/5' : 'border-border'}`}
          data-testid="install-card-android"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="inline-flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-primary" />
              <h3 className="font-display font-bold">Android</h3>
            </div>
            {!ios && (
              <span className="text-xs uppercase tracking-wider bg-primary/20 text-primary px-1.5 py-0.5 rounded-full font-bold">You</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Chrome / Edge / Samsung Internet — installs as a real app icon.
          </p>
          <Button
            onClick={handleAndroidInstall}
            className="w-full rounded-full"
            data-testid="install-android-btn"
          >
            <Download className="w-4 h-4 mr-1.5" />
            {deferredPrompt ? 'Install on Android' : 'How to install'}
          </Button>
          <button
            type="button"
            className="mt-2 text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 w-full justify-center"
            onClick={() => setAndroidExpanded((v) => !v)}
            data-testid="install-android-toggle"
          >
            {androidExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {androidExpanded ? 'Hide steps' : 'See steps'}
          </button>
          {androidExpanded && (
            <ol className="mt-3 text-xs space-y-1.5 text-muted-foreground" data-testid="install-android-steps">
              <li>1. Open this page in <strong>Chrome</strong> on your Android phone.</li>
              <li>2. Tap the <strong>⋮ menu</strong> (top-right).</li>
              <li>3. Tap <strong>Install app</strong> (or "Add to Home screen").</li>
              <li>4. Confirm — SplitEasy now lives on your home screen.</li>
            </ol>
          )}
        </Card>

        {/* iOS card */}
        <Card
          className={`p-4 rounded-2xl border-2 ${ios ? 'border-primary/40 bg-primary/5' : 'border-border'}`}
          data-testid="install-card-ios"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="inline-flex items-center gap-2">
              <Apple className="w-5 h-5 text-primary" />
              <h3 className="font-display font-bold">iPhone / iPad</h3>
            </div>
            {ios && (
              <span className="text-xs uppercase tracking-wider bg-primary/20 text-primary px-1.5 py-0.5 rounded-full font-bold">You</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Safari only — Apple doesn't allow Chrome to install web apps on iOS.
          </p>
          <Button
            variant={ios ? 'default' : 'outline'}
            onClick={() => setIosExpanded((v) => !v)}
            className="w-full rounded-full"
            data-testid="install-ios-btn"
          >
            <Share2 className="w-4 h-4 mr-1.5" />
            {iosExpanded ? 'Hide steps' : 'How to install on iPhone'}
          </Button>
          {iosExpanded && (
            <ol className="mt-3 text-xs space-y-1.5 text-muted-foreground" data-testid="install-ios-steps">
              <li className="inline-flex items-start gap-1.5">
                <span className="font-bold">1.</span>
                <span>Open this page in <strong>Safari</strong> (not Chrome — Apple's rules).</span>
              </li>
              <li className="inline-flex items-start gap-1.5">
                <span className="font-bold">2.</span>
                <span>Tap the <Share2 className="inline w-3 h-3 -mt-0.5" /> <strong>Share</strong> button in the bottom bar.</span>
              </li>
              <li className="inline-flex items-start gap-1.5">
                <span className="font-bold">3.</span>
                <span>Scroll → tap <PlusSquare className="inline w-3 h-3 -mt-0.5" /> <strong>Add to Home Screen</strong>.</span>
              </li>
              <li className="inline-flex items-start gap-1.5">
                <span className="font-bold">4.</span>
                <span>Tap <strong>Add</strong>. Open SplitEasy from your home screen like any app.</span>
              </li>
            </ol>
          )}
        </Card>
      </div>

      <p className="text-center text-xs text-muted-foreground mt-4 max-w-md mx-auto">
        It's a PWA — Progressive Web App. Same UI, push notifications, offline mode. No download size, no app store wait.
      </p>
    </motion.section>
  );
}
